import { Link, type Href } from 'expo-router';
import type { LucideIcon } from 'lucide-react-native';
import * as React from 'react';
import { ScrollView, Switch, View } from 'react-native';

import { Avatar, AvatarFallback, AvatarImage } from '@sixam/ui/avatar';
import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Icon } from '@sixam/ui/icon';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Text } from '@sixam/ui/text';
import { BRAND } from '@sixam/config/brand';

type ButtonVariant = NonNullable<React.ComponentProps<typeof Button>['variant']>;
type ContentInsetAdjustmentBehavior = NonNullable<
  React.ComponentProps<typeof ScrollView>['contentInsetAdjustmentBehavior']
>;

export type ProfileStat = Readonly<{
  compact?: boolean;
  label: string;
  value: string;
}>;

export type ProfileAction = Readonly<{
  href?: Href;
  icon?: LucideIcon;
  key: string;
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
}>;

export type ProfileFormConfig = Readonly<{
  avatarButtonLabel?: string;
  bio: string;
  bioPlaceholder: string;
  contactEmail?: string;
  contactEmailPlaceholder?: string;
  description: string;
  isSaving: boolean;
  isUploadingAvatar?: boolean;
  linksText: string;
  name: string;
  nameLabel?: string;
  namePlaceholder: string;
  onAvatarPress?: () => void;
  onBioChange: (value: string) => void;
  onContactEmailChange?: (value: string) => void;
  onLinksTextChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onPhoneChange?: (value: string) => void;
  onPhoneIsPublicChange?: (value: boolean) => void;
  onSave: () => void;
  phone?: string;
  phoneHelper?: string;
  phoneIsPublic?: boolean;
  phoneIsPublicDescription?: string;
  phoneIsPublicLabel?: string;
  phonePlaceholder?: string;
  saveLabel?: string;
  savingLabel?: string;
  title: string;
}>;

export type ProfileScreenProps = Readonly<{
  actions?: ProfileAction[];
  avatarAlt: string;
  avatarUrl?: string | null;
  bio: string;
  children?: React.ReactNode;
  contentInsetAdjustmentBehavior?: ContentInsetAdjustmentBehavior;
  displayName: string;
  email?: string | null;
  form?: ProfileFormConfig;
  headerAccessory?: React.ReactNode;
  initials: string;
  message?: string | null;
  prelude?: React.ReactNode;
  error?: string | null;
  stats?: ProfileStat[];
  subtitle?: string | null;
  topPadding?: number;
}>;

export function ProfileScreen({
  actions = [],
  avatarAlt,
  avatarUrl,
  bio,
  children,
  contentInsetAdjustmentBehavior = 'automatic',
  displayName,
  email,
  error,
  form,
  headerAccessory,
  initials,
  message,
  prelude,
  stats = [],
  subtitle,
  topPadding = 52,
}: ProfileScreenProps) {
  return (
    <ScrollView
      contentInsetAdjustmentBehavior={contentInsetAdjustmentBehavior}
      className="flex-1 bg-background"
      contentContainerClassName="pb-28"
      scrollEventThrottle={16}
    >
      {prelude}
      <View
        className="px-4 pb-6"
        style={{ backgroundColor: BRAND.color.ink, paddingTop: topPadding }}
      >
        <View className="mt-8 gap-5">
          <Avatar alt={avatarAlt} className="size-28 border-4 border-white">
            {avatarUrl ? <AvatarImage source={{ uri: avatarUrl }} /> : null}
            <AvatarFallback>
              <Text variant="h2">{initials}</Text>
            </AvatarFallback>
          </Avatar>
          <View className="gap-2">
            <View className="flex-row flex-wrap items-center gap-3">
              <Text variant="h2" className="text-white" numberOfLines={2}>
                {displayName}
              </Text>
              {headerAccessory}
            </View>
            {subtitle ? (
              <Text className="text-white/80" numberOfLines={1}>
                {subtitle}
              </Text>
            ) : null}
            {email ? (
              <Text className="text-white/80" numberOfLines={1} selectable>
                {email}
              </Text>
            ) : null}
            <Text className="max-w-xl text-base leading-6 text-white">{bio}</Text>
          </View>
        </View>

        {stats.length > 0 ? (
          <View className="mt-6 flex-row border-t border-white/15 pt-4">
            {stats.map((stat) => (
              <ProfileStatBlock key={stat.label} {...stat} />
            ))}
          </View>
        ) : null}
      </View>

      <View className="gap-4 bg-background px-4 pt-4 sm:px-6">
        {actions.length > 0 ? (
          <View className="gap-3 sm:flex-row">
            {actions.map((action) => (
              <ProfileActionButton key={action.key} action={action} />
            ))}
          </View>
        ) : null}

        {message ? <Text variant="muted">{message}</Text> : null}
        {error ? (
          <Text className="text-sm text-destructive" selectable>
            {error}
          </Text>
        ) : null}

        {form ? <ProfileForm form={form} /> : null}

        {children}
      </View>
    </ScrollView>
  );
}

function ProfileActionButton({ action }: Readonly<{ action: ProfileAction }>) {
  const button = (
    <Button
      className="h-14 sm:flex-1"
      onPress={action.onPress}
      variant={action.variant ?? 'default'}
    >
      {action.icon ? <Icon as={action.icon} className="size-4 text-white" /> : null}
      <Text>{action.label}</Text>
    </Button>
  );

  if (action.href) {
    return (
      <Link href={action.href} asChild>
        {button}
      </Link>
    );
  }

  return button;
}

export function ProfileForm({ form }: Readonly<{ form: ProfileFormConfig }>) {
  const showPhoneVisibility =
    form.phoneIsPublic !== undefined && form.onPhoneIsPublicChange !== undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{form.title}</CardTitle>
        <CardDescription>{form.description}</CardDescription>
      </CardHeader>
      <CardContent className="gap-4">
        {form.onAvatarPress ? (
          <Button disabled={form.isUploadingAvatar} onPress={form.onAvatarPress} variant="outline">
            <Text>
              {form.isUploadingAvatar ? 'Uploading...' : (form.avatarButtonLabel ?? 'Edit photo')}
            </Text>
          </Button>
        ) : null}
        <View className="gap-1.5">
          <Label>{form.nameLabel ?? 'Name'}</Label>
          <Input
            value={form.name}
            onChangeText={form.onNameChange}
            placeholder={form.namePlaceholder}
          />
        </View>
        <View className="gap-1.5">
          <Label>Description</Label>
          <Input
            value={form.bio}
            onChangeText={form.onBioChange}
            placeholder={form.bioPlaceholder}
            multiline
            numberOfLines={4}
            className="h-24 py-2"
          />
        </View>
        <View className="gap-1.5">
          <Label>Links</Label>
          <Input
            value={form.linksText}
            onChangeText={form.onLinksTextChange}
            placeholder="Instagram https://instagram.com/6ambkk"
            multiline
            numberOfLines={3}
            className="h-20 py-2"
            autoCapitalize="none"
          />
        </View>
        {form.onContactEmailChange ? (
          <View className="gap-1.5">
            <Label>Contact email</Label>
            <Input
              value={form.contactEmail}
              onChangeText={form.onContactEmailChange}
              placeholder={form.contactEmailPlaceholder ?? 'crew@6ambkk.com'}
              autoCapitalize="none"
              keyboardType="email-address"
              textContentType="emailAddress"
            />
          </View>
        ) : null}
        {form.onPhoneChange ? (
          <View className="gap-1.5">
            <Label>Phone</Label>
            <Input
              value={form.phone}
              onChangeText={form.onPhoneChange}
              placeholder={form.phonePlaceholder ?? '+66 81 234 5678'}
              keyboardType="phone-pad"
              textContentType="telephoneNumber"
            />
            {form.phoneHelper ? <Text variant="muted">{form.phoneHelper}</Text> : null}
          </View>
        ) : null}
        {showPhoneVisibility ? (
          <View className="flex-row items-center justify-between gap-4">
            <View className="min-w-0 flex-1">
              <Text variant="large">{form.phoneIsPublicLabel ?? 'Public phone'}</Text>
              <Text variant="muted">
                {form.phoneIsPublicDescription ?? 'Show this number on the public profile.'}
              </Text>
            </View>
            <Switch value={form.phoneIsPublic} onValueChange={form.onPhoneIsPublicChange} />
          </View>
        ) : null}
        <Button disabled={form.isSaving} onPress={form.onSave}>
          <Text>
            {form.isSaving ? (form.savingLabel ?? 'Saving...') : (form.saveLabel ?? 'Save profile')}
          </Text>
        </Button>
      </CardContent>
    </Card>
  );
}

function ProfileStatBlock({ compact, label, value }: ProfileStat) {
  return (
    <View className="min-w-0 flex-1 border-r border-white/15 pr-3">
      <Text
        adjustsFontSizeToFit
        className={`font-display font-bold text-white ${compact ? 'text-xl' : 'text-2xl'}`}
        numberOfLines={1}
      >
        {value}
      </Text>
      <Text className="text-sm text-white/80">{label}</Text>
    </View>
  );
}
